import {
  Badge,
  Box,
  Code,
  Collapse,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { AppIcons } from "constants/icons";
import { JinjaEnv, JinjaVariable, JinjaFunction, JinjaObject } from "build-ts/jinja";
import { DarwinAgentType } from "build-ts/common";
import { DarwinToolType } from "build-ts/darwin_agent";
import CopyButton from "components/common/CopyButton";

// Types for navigation items
type NavItemType =
  | "agent_var"
  | "tool_var"
  | "env_var"
  | "object"
  | "object_property"
  | "object_method"
  | "utility_function";

interface NavItem {
  id: string;
  name: string;
  type: NavItemType;
  category: string;
  data: JinjaVariable | JinjaFunction | JinjaObject;
  parentName?: string; // For properties/methods to reference their parent object
  searchableText: string; // For search functionality
}

// Sidebar Item Component
interface SidebarItemProps {
  item: NavItem;
  isSelected: boolean;
  onClick: () => void;
  indent?: number;
  showParent?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ item, isSelected, onClick, indent = 0, showParent = false }) => {
  const getBgColor = () => {
    if (isSelected) {
      return item.type === "object" ? "blue.50" : "purple.50";
    }
    return "transparent";
  };

  const getIconConfig = () => {
    switch (item.type) {
      case "agent_var":
      case "tool_var":
      case "env_var":
        return { icon: AppIcons.variable, color: "purple.500" };
      case "object":
        return { icon: AppIcons.dataObject, color: "green.500" };
      case "object_property":
        return { icon: AppIcons.property, color: "blue.500" };
      case "object_method":
      case "utility_function":
        return { icon: AppIcons.mathFunction, color: "orange.500" };
      default:
        return { icon: AppIcons.variable, color: "gray.500" };
    }
  };

  const iconConfig = getIconConfig();

  return (
    <Box
      px={3}
      py={1}
      pl={3 + indent * 4}
      bg={getBgColor()}
      cursor="pointer"
      onClick={onClick}
      _hover={{ bg: isSelected ? getBgColor() : "gray.100" }}
      transition="all 0.2s"
      borderLeft={isSelected ? "3px solid" : "none"}
      borderColor={item.type === "object" ? "blue.400" : "purple.500"}
    >
      <HStack spacing={2} align="flex-start">
        <Icon as={iconConfig.icon} color={iconConfig.color} boxSize={5} flexShrink={0} />
        <VStack spacing={0} align="flex-start" flex={1} minW={0}>
          <Text fontSize="sm" fontWeight={item.type === "object" ? "semibold" : "normal"} noOfLines={1} w="100%" mb={0}>
            {item.name}
            {item.type === "object_method" || item.type === "utility_function" ? "()" : ""}
          </Text>
          {showParent && item.parentName && (
            <Text fontSize="xs" color="gray.500" noOfLines={1} w="100%" mb={0}>
              in {item.parentName}
            </Text>
          )}
        </VStack>
      </HStack>
    </Box>
  );
};

// Detail View Component
interface DetailViewProps {
  item: NavItem | null;
}

const DetailView: React.FC<DetailViewProps> = ({ item }) => {
  if (!item) {
    return (
      <Flex h="100%" align="center" justify="center">
        <VStack spacing={2}>
          <Text color="gray.400" fontSize="sm" mb={0}>
            Select an item to view details
          </Text>
          <Text color="gray.400" fontSize="xs" mb={0}>
            Choose from the sidebar on the left
          </Text>
        </VStack>
      </Flex>
    );
  }

  const renderVariableDetail = (variable: JinjaVariable) => {
    const iconConfig =
      item.type === "object_property"
        ? { icon: AppIcons.property, color: "blue.500" }
        : { icon: AppIcons.variable, color: "purple.500" };

    return (
      <VStack align="stretch" spacing={4}>
        <Box>
          <HStack spacing={3} mb={2}>
            <Icon as={iconConfig.icon} color={iconConfig.color} boxSize={6} />
            <Code fontSize="md" fontWeight="semibold" bg="gray.100" px={2} py={1}>
              {variable.name || ""}
            </Code>
            <Badge colorScheme="gray">{variable.type || "Any"}</Badge>
          </HStack>
          {variable.description && (
            <Text fontSize="sm" color="gray.700" mb={0}>
              {variable.description}
            </Text>
          )}
        </Box>

        {variable.example && (
          <Box>
            <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
              Example
            </Text>
            <Box p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
              <Code fontSize="sm" color="gray.800" bg="transparent" whiteSpace="pre-wrap">
                {variable.example}
              </Code>
            </Box>
          </Box>
        )}
      </VStack>
    );
  };

  const renderFunctionDetail = (func: JinjaFunction) => (
    <VStack align="stretch" spacing={4}>
      <Box>
        <HStack spacing={3} mb={2}>
          <Icon as={AppIcons.mathFunction} color="orange.500" boxSize={6} />
          <Code fontSize="md" fontWeight="semibold" bg="gray.100" px={2} py={1}>
            {func.name || ""}()
          </Code>
          <Badge colorScheme="gray">{func.return_type || "Any"}</Badge>
        </HStack>
        {func.description && (
          <Text fontSize="sm" color="gray.700" mb={0}>
            {func.description}
          </Text>
        )}
      </Box>

      {func.parameters && func.parameters.length > 0 && (
        <Box>
          <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
            Parameters
          </Text>
          <Box p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
            <VStack align="stretch" spacing={1}>
              {func.parameters.map((param, idx) => (
                <Code key={idx} fontSize="sm" bg="gray.100" px={2} py={1}>
                  {param}
                </Code>
              ))}
            </VStack>
          </Box>
        </Box>
      )}

      {func.example && (
        <Box>
          <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
            Example
          </Text>
          <Box p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
            <Code fontSize="sm" color="gray.800" bg="transparent" whiteSpace="pre-wrap">
              {func.example}
            </Code>
          </Box>
        </Box>
      )}
    </VStack>
  );

  const renderObjectDetail = (obj: JinjaObject) => (
    <VStack align="stretch" spacing={4}>
      <Box>
        <HStack spacing={3} mb={2}>
          <Icon as={AppIcons.dataObject} color="green.500" boxSize={6} />{" "}
          <Code fontSize="md" fontWeight="semibold" bg="gray.100" px={2} py={1}>
            {obj.type || ""}
          </Code>
          <Badge colorScheme="green">Class</Badge>
        </HStack>
        {obj.description && (
          <Text fontSize="sm" color="gray.700" mb={0}>
            {obj.description}
          </Text>
        )}
      </Box>

      {obj.object_variables && obj.object_variables.length > 0 && (
        <Box>
          <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3}>
            Properties ({obj.object_variables.length})
          </Text>
          <VStack align="stretch" spacing={2}>
            {obj.object_variables.map(variable => (
              <Box key={variable.name} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                <HStack spacing={2} mb={1}>
                  <Icon as={AppIcons.property} color="blue.500" boxSize={4} />
                  <Code fontSize="sm" fontWeight="medium" bg="white" px={2} py={0.5}>
                    {variable.name || ""}
                  </Code>
                  <Badge size="sm" colorScheme="gray">
                    {variable.type || "Any"}
                  </Badge>
                </HStack>
                {variable.description && (
                  <Text fontSize="xs" color="gray.600" mb={0} ml={6}>
                    {variable.description}
                  </Text>
                )}
              </Box>
            ))}
          </VStack>
        </Box>
      )}

      {obj.object_functions && obj.object_functions.length > 0 && (
        <Box>
          <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3}>
            Methods ({obj.object_functions.length})
          </Text>
          <VStack align="stretch" spacing={2}>
            {obj.object_functions.map(func => (
              <Box key={func.name} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                <HStack spacing={2} mb={1}>
                  <Icon as={AppIcons.mathFunction} color="orange.500" boxSize={4} />
                  <Code fontSize="sm" fontWeight="medium" bg="white" px={2} py={0.5}>
                    {func.name || ""}()
                  </Code>
                  <Badge size="sm" colorScheme="gray">
                    {func.return_type || "Any"}
                  </Badge>
                </HStack>
                {func.description && (
                  <Text fontSize="xs" color="gray.600" mb={0} ml={6}>
                    {func.description}
                  </Text>
                )}
                {func.parameters && func.parameters.length > 0 && (
                  <Text fontSize="xs" color="gray.500" mt={1} mb={0} ml={6}>
                    Parameters: {func.parameters.join(", ")}
                  </Text>
                )}
              </Box>
            ))}
          </VStack>
        </Box>
      )}
    </VStack>
  );

  const renderContent = () => {
    if (item.type === "object") {
      return renderObjectDetail(item.data as JinjaObject);
    } else if (item.type === "object_method" || item.type === "utility_function") {
      return renderFunctionDetail(item.data as JinjaFunction);
    } else {
      return renderVariableDetail(item.data as JinjaVariable);
    }
  };

  return (
    <Box p={6} h="100%" overflowY="auto">
      <Badge colorScheme="gray" mb={4}>
        {item.category}
      </Badge>
      {renderContent()}
    </Box>
  );
};

interface DocModalProps {
  isDocsOpen: boolean;
  onDocsClose: () => void;
  isLoadingDoc: boolean;
  availableVariables: JinjaEnv | null;
  agentType?: DarwinAgentType;
  toolType?: DarwinToolType;
  isSkillMode?: boolean;
}

const DocModal: React.FC<DocModalProps> = ({
  isDocsOpen,
  onDocsClose,
  isLoadingDoc,
  availableVariables,
  agentType,
  toolType,
  isSkillMode = false,
}) => {
  const [fullDocText, setFullDocText] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  // Extract data from JinjaEnv
  const objects = availableVariables?.objects || [];
  const utilityFunctions = availableVariables?.utility_functions || [];
  const envVariables = availableVariables?.env_variables || [];

  // Filter agent input variables based on agent type — hidden in skill mode
  const agentInputVariables: JinjaVariable[] = (() => {
    if (isSkillMode || !agentType || !availableVariables) return [];
    const agentVars = availableVariables.agent_input_variables?.find(av => av.agent_type === agentType);
    return agentVars?.input_variables || [];
  })();

  // Filter tool input variables based on tool type
  const toolInputVariables: JinjaVariable[] = (() => {
    if (!toolType || !availableVariables) return [];
    const toolVars = (availableVariables as any).tool_input_variables?.find((tv: any) => tv.tool_type === toolType);
    return toolVars?.input_variables || [];
  })();

  // Skill prompt input variables — only shown in skill mode
  const skillInputVariables: JinjaVariable[] = isSkillMode ? availableVariables?.skill_input_variables || [] : [];

  // Build navigation items from all available data
  const allNavItems = useMemo(() => {
    const items: NavItem[] = [];

    // Add skill input variables (skill mode only)
    skillInputVariables.forEach(variable => {
      items.push({
        id: `skill_var_${variable.name}`,
        name: variable.name || "",
        type: "agent_var",
        category: "Skill Input Variables",
        data: variable,
        searchableText: [variable.name, variable.description, variable.type, variable.example]
          .filter(Boolean)
          .join(" ")
          .toLowerCase(),
      });
    });

    // Add agent input variables
    agentInputVariables.forEach(variable => {
      items.push({
        id: `agent_var_${variable.name}`,
        name: variable.name || "",
        type: "agent_var",
        category: "Agent Input Variables",
        data: variable,
        searchableText: [variable.name, variable.description, variable.type, variable.example]
          .filter(Boolean)
          .join(" ")
          .toLowerCase(),
      });
    });

    // Add tool input variables
    toolInputVariables.forEach(variable => {
      items.push({
        id: `tool_var_${toolType}_${variable.name}`,
        name: variable.name || "",
        type: "tool_var",
        category: "Tool Input Variables",
        data: variable,
        searchableText: [variable.name, variable.description, variable.type, variable.example]
          .filter(Boolean)
          .join(" ")
          .toLowerCase(),
      });
    });

    // Add environment variables
    envVariables.forEach(variable => {
      items.push({
        id: `env_var_${variable.name}`,
        name: variable.name || "",
        type: "env_var",
        category: "Environment Variables",
        data: variable,
        searchableText: [variable.name, variable.description, variable.type, variable.example]
          .filter(Boolean)
          .join(" ")
          .toLowerCase(),
      });
    });

    // Add objects and their properties/methods
    objects.forEach(obj => {
      const objId = `object_${obj.name}`;
      items.push({
        id: objId,
        name: obj.type || obj.name || "",
        type: "object",
        category: "Classes & Objects",
        data: obj,
        searchableText: `${obj.name} ${obj.type || ""} ${obj.description || ""}`.toLowerCase(),
      });

      // Add object properties
      (obj.object_variables || []).forEach(variable => {
        items.push({
          id: `${objId}_prop_${variable.name}`,
          name: variable.name || "",
          type: "object_property",
          category: "Classes & Objects",
          data: variable,
          parentName: obj.type || obj.name || "",
          searchableText: [obj.type, obj.name, variable.name, variable.description, variable.type]
            .filter(Boolean)
            .join(" ")
            .toLowerCase(),
        });
      });

      // Add object methods
      (obj.object_functions || []).forEach(func => {
        items.push({
          id: `${objId}_method_${func.name}`,
          name: func.name || "",
          type: "object_method",
          category: "Classes & Objects",
          data: func,
          parentName: obj.type || obj.name || "",
          searchableText: [obj.type, obj.name, func.name, func.description, func.return_type]
            .filter(Boolean)
            .join(" ")
            .toLowerCase(),
        });
      });
    });

    // Add utility functions
    utilityFunctions.forEach(func => {
      items.push({
        id: `utility_${func.name}`,
        name: func.name || "",
        type: "utility_function",
        category: "Utility Functions",
        data: func,
        searchableText: [func.name, func.description, func.return_type, ...(func.parameters || [])]
          .filter(Boolean)
          .join(" ")
          .toLowerCase(),
      });
    });

    return items;
  }, [skillInputVariables, agentInputVariables, toolInputVariables, toolType, envVariables, objects, utilityFunctions]);

  // Filter items based on search term
  const filteredNavItems = useMemo(() => {
    if (!searchTerm.trim()) return allNavItems;
    const search = searchTerm.toLowerCase();
    return allNavItems.filter(item => item.searchableText.includes(search));
  }, [allNavItems, searchTerm]);

  // Group filtered items by category
  const groupedItems = useMemo(() => {
    const groups: Record<string, NavItem[]> = {};
    filteredNavItems.forEach(item => {
      if (!groups[item.category]) {
        groups[item.category] = [];
      }
      groups[item.category].push(item);
    });
    return groups;
  }, [filteredNavItems]);

  // Get currently selected item
  const selectedItem = useMemo(() => {
    return allNavItems.find(item => item.id === selectedItemId) || null;
  }, [allNavItems, selectedItemId]);

  // Toggle category collapse
  const toggleCategory = (category: string) => {
    setCollapsedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  // Get the complete doc
  const generateFullDocs = () => {
    if (!availableVariables) return;
    setFullDocText(JSON.stringify(JinjaEnv.toJSON(availableVariables)));
  };

  // Run doc text initialization
  useEffect(() => {
    generateFullDocs();
  }, [availableVariables]);

  return (
    <Modal isOpen={isDocsOpen} onClose={onDocsClose} size="7xl" isCentered>
      <ModalOverlay />
      <ModalContent bg="white" maxH="90vh" h="90vh" w="90vw" maxW="90vw">
        <ModalHeader borderBottom="1px solid" borderColor="gray.200" bg="white" p={4} flexShrink={0}>
          <HStack spacing={2} mb={3}>
            <Icon as={AppIcons.curlyBrackets} boxSize="20px" />
            <Text fontSize="lg" fontWeight="bold" mb={0}>
              Jinja Variables Documentation
            </Text>
            <Box ml={2}>
              <CopyButton value={fullDocText} />
            </Box>
          </HStack>

          {/* Search Bar */}
          <Box>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <Icon as={AppIcons.search} color="gray.800" />
              </InputLeftElement>
              <Input
                placeholder="Search variables, classes, methods..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                bg="gray.50"
                border="1px solid"
                borderColor="gray.200"
                _focus={{ bg: "white", borderColor: "purple.300" }}
              />
            </InputGroup>
          </Box>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody p={0} overflow="hidden" flex={1}>
          {isLoadingDoc ? (
            <Flex h="100%" align="center" justify="center">
              <Text color="gray.500" fontSize="sm" mb={0}>
                Loading documentation...
              </Text>
            </Flex>
          ) : allNavItems.length === 0 ? (
            <Flex h="100%" align="center" justify="center">
              <Text color="gray.500" fontSize="sm" mb={0}>
                No documentation available
              </Text>
            </Flex>
          ) : (
            <Flex h="100%">
              {/* Sidebar */}
              <Box w="300px" borderRight="1px solid" borderColor="gray.200" overflowY="auto" bg="white" h="100%">
                {filteredNavItems.length === 0 ? (
                  <Box p={4} textAlign="center">
                    <Text fontSize="xs" color="gray.500" mb={0}>
                      No results found
                    </Text>
                  </Box>
                ) : (
                  <VStack spacing={0} align="stretch">
                    {Object.entries(groupedItems).map(([category, items]) => {
                      const isCollapsed = collapsedCategories.has(category);
                      return (
                        <Box key={category}>
                          {/* Category Header */}
                          <HStack
                            px={3}
                            py={2}
                            bg="gray.100"
                            borderBottom="1px solid"
                            borderColor="gray.200"
                            position="sticky"
                            top={0}
                            zIndex={1}
                            cursor="pointer"
                            onClick={() => toggleCategory(category)}
                            _hover={{ bg: "gray.200" }}
                            transition="background 0.2s"
                            justify="space-between"
                          >
                            <Text fontSize="xs" fontWeight="bold" color="gray.700" textTransform="uppercase" mb={0}>
                              {category}
                            </Text>
                            <Icon
                              as={isCollapsed ? AppIcons.chevronRight : AppIcons.chevronDown}
                              color="gray.600"
                              boxSize={3}
                            />
                          </HStack>

                          {/* Category Items */}
                          <Collapse in={!isCollapsed} animateOpacity>
                            {items.map(item => (
                              <SidebarItem
                                key={item.id}
                                item={item}
                                isSelected={selectedItemId === item.id}
                                onClick={() => setSelectedItemId(item.id)}
                                indent={item.parentName ? 1 : 0}
                                showParent={searchTerm.trim().length > 0}
                              />
                            ))}
                          </Collapse>
                        </Box>
                      );
                    })}
                  </VStack>
                )}
              </Box>

              {/* Main Content Area */}
              <Box flex={1} bg="white" overflowY="auto" h="100%">
                <DetailView item={selectedItem} />
              </Box>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default DocModal;
